/**
 * OptionService
 *
 * RESPONSIBILITIES:
 * - All option mathematics (Black-Scholes pricing, Greeks, intrinsic value)
 * - Option contract creation and validation
 * - Position metrics calculation and PnL analysis
 * - Order book pricing simulation (bid/ask spreads)
 *
 * SHOULD NOT:
 * - Handle order management or execution
 * - Manage account balances or transactions
 * - Store or persist data (except for calculations)
 */

import { getPriceService } from '@hooks/usePrices';
import type { Asset } from '@shared/types';
import { getSeededRandomValue } from '@utils/deterministicRandom';
import { blackScholesPrice, getImpliedVolatility } from '@utils/option-math';
export type OptionType = 'CALL' | 'PUT';

export interface Option {
  readonly asset: Asset;
  readonly type: OptionType;
  readonly strikePrice: number;
  readonly expiryTimestamp: number; // Unix timestamp in ms
  readonly purchasePrice: number;
  readonly quantity: number;
  readonly purchaseTimestamp: number;
}

export interface OptionPricing {
  readonly callPrice: number;
  readonly putPrice: number;
}

export interface OptionMetrics {
  readonly currentPrice: number;
  readonly intrinsicValue: number;
  readonly timeValue: number;
  readonly breakeven: number | null;
  readonly inTheMoney: boolean;
  readonly timeToExpiry: number; // hours
  readonly daysToExpiry: number;
  readonly expired: boolean;
}

export interface PositionMetrics extends OptionMetrics {
  readonly purchasePrice: number;
  readonly quantity: number;
  readonly totalCost: number;
  readonly currentValue: number;
  readonly pnlPerContract: number;
  readonly pnlTotal: number;
  readonly roi: number;
  readonly profitable: boolean;
  readonly expiryValue: number;
  readonly expiryPnl: number;
  readonly expiryRoi: number;
  readonly statusLabel: string;
  readonly statusColor: string;
}

export interface SettlementResult {
  readonly intrinsicValue: number;
  readonly pnlPerContract: number;
  readonly pnlTotal: number;
}

export interface BidAskPrices {
  readonly bid: number;
  readonly ask: number;
  readonly mid: number;
  readonly spreadPercent?: number;
}

export class OptionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OptionError';
  }
}

export class OptionCalculator {
  static createOption(params: {
    asset: Asset;
    type: OptionType;
    strikePrice: number;
    expiryTimestamp: number;
    purchasePrice: number;
    quantity: number;
    purchaseTimestamp: number;
  }): Option {
    // Validation
    if (params.strikePrice <= 0) {
      throw new OptionError('Strike price must be positive');
    }
    if (params.purchasePrice <= 0) {
      throw new OptionError('Purchase price must be positive');
    }
    if (params.quantity <= 0) {
      throw new OptionError('Quantity must be positive');
    }

    return {
      asset: params.asset,
      type: params.type,
      strikePrice: params.strikePrice,
      expiryTimestamp: params.expiryTimestamp,
      purchasePrice: params.purchasePrice,
      quantity: params.quantity,
      purchaseTimestamp: params.purchaseTimestamp,
    };
  }

  static getTimeToExpiry(
    option: Option,
    currentTime: number = Date.now(),
  ): number {
    const ms = Math.max(0, option.expiryTimestamp - currentTime);
    return ms / (1000 * 60 * 60); // Convert to hours
  }

  static getDaysToExpiry(
    option: Option,
    currentTime: number = Date.now(),
  ): number {
    return this.getTimeToExpiry(option, currentTime) / 24;
  }

  static isExpired(option: Option, currentTime: number = Date.now()): boolean {
    return option.expiryTimestamp <= currentTime;
  }

  static isCall(option: Option): boolean {
    return option.type === 'CALL';
  }

  static isPut(option: Option): boolean {
    return option.type === 'PUT';
  }

  static getIntrinsicValue(option: Option, spotPrice: number): number {
    return this.isCall(option)
      ? Math.max(0, spotPrice - option.strikePrice)
      : Math.max(0, option.strikePrice - spotPrice);
  }

  static getBreakeven(option: Option): number {
    if (!option.purchasePrice) {
      throw new OptionError(
        'Cannot calculate breakeven without purchase price',
      );
    }

    return this.isCall(option)
      ? option.strikePrice + option.purchasePrice
      : option.strikePrice - option.purchasePrice;
  }

  /**
   * Calculate realistic breakeven: where spot price needs to be for bid price to equal purchase price
   * Accounts for bid/ask spread - this is what you actually need for profitability when selling
   */
  static getRealisticBreakeven(
    option: Option,
    spotPrice: number,
    currentTime: number,
    volatility: number,
  ): number {
    if (!option.purchasePrice) {
      throw new OptionError(
        'Cannot calculate realistic breakeven without purchase price',
      );
    }

    const targetPrice = option.purchasePrice;
    let breakeven = spotPrice;

    // Set search bounds based on option type and current spot
    let low = Math.max(0.01, spotPrice * 0.25);
    let high = spotPrice * 2;

    // Simple bisection search to find exact breakeven using bid prices
    for (let i = 0; i < 30; i++) {
      const testSpot = (low + high) / 2;
      breakeven = testSpot;

      try {
        // Use the same pricing logic as PnL service - through getPositionMetrics
        const testMetrics = this.getPositionMetrics(
          option,
          testSpot,
          currentTime,
          volatility,
        );

        // Use the same current price calculation as PnL service
        const bidPrice = testMetrics.currentPrice;

        // Adjust search bounds using bid price
        if (bidPrice < targetPrice) {
          if (this.isCall(option)) {
            low = testSpot; // Need higher spot for calls
          } else {
            high = testSpot; // Need lower spot for puts
          }
        } else {
          if (this.isCall(option)) {
            high = testSpot; // Need lower spot for calls
          } else {
            low = testSpot; // Need higher spot for puts
          }
        }
      } catch (error) {
        console.warn(
          'Error in bisection search for realistic breakeven:',
          error,
        );
        break;
      }
    }

    return breakeven;
  }

  /**
   * Calculate current breakeven: where spot price needs to be for option to be worth purchase price right now
   * Uses bisection search to find the exact spot price where current option value = purchase price
   */
  static getCurrentBreakeven(
    option: Option,
    spotPrice: number,
    currentTime: number,
    volatility: number,
  ): number {
    if (!option.purchasePrice) {
      throw new OptionError(
        'Cannot calculate current breakeven without purchase price',
      );
    }

    const targetPrice = option.purchasePrice;
    let breakeven = spotPrice;

    // Set search bounds based on option type and current spot
    let low = Math.max(0.01, spotPrice * 0.25);
    let high = spotPrice * 2;

    // Simple bisection search to find exact breakeven
    for (let i = 0; i < 30; i++) {
      const testSpot = (low + high) / 2;
      breakeven = testSpot;

      try {
        // Calculate option value at this test spot price
        const testPricing = this.getPricing({
          option,
          spotPrice: testSpot,
          currentTime,
          volatility,
        });

        const testValue = this.isCall(option)
          ? testPricing.callPrice
          : testPricing.putPrice;

        // Adjust search bounds
        if (testValue < targetPrice) {
          if (this.isCall(option)) {
            low = testSpot; // Need higher spot for calls
          } else {
            high = testSpot; // Need lower spot for puts
          }
        } else {
          if (this.isCall(option)) {
            high = testSpot; // Need lower spot for calls
          } else {
            low = testSpot; // Need higher spot for puts
          }
        }
      } catch (error) {
        console.warn('Error in bisection search for current breakeven:', error);
        break;
      }
    }

    return breakeven;
  }

  /**
   * Calculate order book pricing (bid/ask/mid) for an option
   */
  static calculatePricing(
    asset: Asset,
    strikePrice: number,
    optionType: 'CALL' | 'PUT',
    expiryTimestamp: number,
    volatility: number,
    spotPriceOverride?: number,
  ): BidAskPrices | null {
    const priceService = getPriceService();
    const spotPriceData = priceService.getPrice(asset);

    // Use override if provided, otherwise fall back to priceService
    const spotPrice = spotPriceOverride ?? spotPriceData?.price;

    if (!spotPrice || spotPrice <= 0) {
      return null;
    }

    if (!volatility || volatility <= 0) {
      return null;
    }

    try {
      // Calculate mid price using Black-Scholes
      const option = this.createOption({
        asset: asset,
        type: optionType,
        strikePrice,
        expiryTimestamp,
        purchasePrice: spotPrice,
        quantity: 1,
        purchaseTimestamp: Date.now(),
      });

      const midPrice = this.getCurrentPrice(
        option,
        spotPrice,
        Date.now(),
        volatility,
      );

      // Calculate order book pricing (bid/ask) from mid price
      const orderBook = this.calculateOrderBookPricing(
        midPrice,
        spotPrice,
        strikePrice,
        expiryTimestamp,
      );

      const result = {
        bid: orderBook.bid,
        ask: orderBook.ask,
        mid: midPrice,
        spreadPercent: orderBook.spreadPercent,
      };
      return result;
    } catch (error) {
      console.warn('[OptionCalculator] Error calculating option price:', error);
      return null;
    }
  }

  /**
   * Calculate order book pricing (bid/ask levels) from mid price
   */
  static calculateOrderBookPricing(
    midPrice: number,
    spotPrice: number,
    strikePrice: number,
    expiryTimestamp: number,
  ): { bid: number; ask: number; spreadPercent: number } {
    // Deterministic random based on strike price and expiry date
    const randomFactor = getSeededRandomValue(strikePrice, expiryTimestamp);

    // Base spread: $5 total spread (±$2.50 each side)
    const baseDollarSpread = 5.0;

    // Add randomization: ±30% variation
    const variation = 0.3;
    const randomizedSpread =
      baseDollarSpread * (1 + (randomFactor - 0.5) * variation * 2);

    // Calculate what percentage that would be
    const dollarSpreadPercent = (randomizedSpread / midPrice) * 100;

    // Cap at 10% maximum but start triggering reduction at 6%
    const softCapPercent = 6.0;
    const maxSpreadPercent = 10.0;

    let finalSpreadPercent;
    if (dollarSpreadPercent <= softCapPercent) {
      finalSpreadPercent = dollarSpreadPercent;
    } else if (dollarSpreadPercent <= maxSpreadPercent) {
      // Gradually reduce between 6% and 10%
      const excessRatio =
        (dollarSpreadPercent - softCapPercent) /
        (maxSpreadPercent - softCapPercent);
      const reductionFactor = 1 - excessRatio * 0.4; // Up to 40% reduction
      finalSpreadPercent = dollarSpreadPercent * reductionFactor;
    } else {
      finalSpreadPercent = maxSpreadPercent * 0.6; // 60% of max when hitting cap
    }

    const totalSpread = midPrice * (finalSpreadPercent / 100);

    // Calculate bid/ask prices
    const halfSpread = totalSpread * 0.5;
    const bid = Math.max(midPrice - halfSpread, 0.01); // Minimum bid of $0.01
    const ask = midPrice + halfSpread;

    return { bid, ask, spreadPercent: finalSpreadPercent };
  }

  static isInTheMoney(option: Option, spotPrice: number): boolean {
    return this.isCall(option)
      ? spotPrice > option.strikePrice
      : spotPrice < option.strikePrice;
  }

  static isProfitable(option: Option, spotPrice: number): boolean {
    if (!option.purchasePrice) {
      return false;
    }
    const breakeven = this.getBreakeven(option);
    return this.isCall(option)
      ? spotPrice >= breakeven
      : spotPrice <= breakeven;
  }

  static getPricing({
    option,
    spotPrice,
    currentTime,
    volatility,
  }: {
    option: Option;
    spotPrice: number;
    currentTime: number;
    volatility: number;
  }): OptionPricing {
    const timeToExpiryHours = this.getTimeToExpiry(option, currentTime);
    // Convert hours to years for Black-Scholes (8760 = 365 * 24 hours/year)
    const timeToExpiryYears = timeToExpiryHours / (365 * 24);

    // Apply moneyness-based volatility skew
    // This creates a "volatility smile" where OTM options have higher volatility
    // Skew factor of 10.0 creates a U-shaped smile typical in crypto markets
    const adjustedVolatility = getImpliedVolatility(
      spotPrice,
      option.strikePrice,
      volatility,
      10.0, // Skew factor - adjust to make wings more/less expensive
    );

    const callPrice = blackScholesPrice({
      optionType: 'call',
      underlyingPrice: spotPrice,
      strikePrice: option.strikePrice,
      timeToExpiry: timeToExpiryYears,
      volatility: adjustedVolatility,
      riskFreeRate: 0.045,
    });
    const putPrice = blackScholesPrice({
      optionType: 'put',
      underlyingPrice: spotPrice,
      strikePrice: option.strikePrice,
      timeToExpiry: timeToExpiryYears,
      volatility: adjustedVolatility,
      riskFreeRate: 0.045,
    });

    return {
      callPrice,
      putPrice,
    };
  }

  /**
   * Get minimum option price floor for a specific asset
   * Prevents unrealistic percentage gains from very small prices
   * 
   * Asset-specific minimums:
   * - SOL: $0.10
   * - ETH: $1.00
   * - BTC: $20.00
   */
  static getMinOptionPrice(asset: Asset): number {
    switch (asset) {
      case 'SOL':
        return 0.10;
      case 'ETH':
        return 1.0;
      case 'BTC':
        return 20.0;
      default:
        return 0.01; // Fallback for unknown assets
    }
  }

  static getCurrentPrice(
    option: Option,
    spotPrice: number,
    currentTime: number,
    volatility: number,
  ): number {
    const pricing = this.getPricing({
      option,
      spotPrice,
      currentTime,
      volatility,
    });
    const price = this.isCall(option) ? pricing.callPrice : pricing.putPrice;
    // Floor at minimum price to prevent unrealistic percentage gains
    const minPrice = this.getMinOptionPrice(option.asset);
    return price > 0 ? Math.max(price, minPrice) : 0;
  }

  static getMetrics(
    option: Option,
    spotPrice: number,
    currentTime: number = Date.now(),
    volatility: number,
  ): OptionMetrics {
    const currentPrice = this.getCurrentPrice(
      option,
      spotPrice,
      currentTime,
      volatility,
    );
    const intrinsicValue = this.getIntrinsicValue(option, spotPrice);
    const timeValue = currentPrice - intrinsicValue;
    const timeToExpiry = this.getTimeToExpiry(option, currentTime);
    const daysToExpiry = this.getDaysToExpiry(option, currentTime);
    const expired = this.isExpired(option, currentTime);

    let breakeven: number | null = null;
    try {
      breakeven = this.getBreakeven(option);
    } catch {
      // No purchase price available
    }

    return {
      currentPrice,
      intrinsicValue,
      timeValue,
      breakeven,
      inTheMoney: this.isInTheMoney(option, spotPrice),
      timeToExpiry,
      daysToExpiry,
      expired,
    };
  }

  static getPositionMetrics(
    option: Option,
    spotPrice: number,
    currentTime: number,
    volatility: number,
  ): PositionMetrics {
    if (!option.purchasePrice || !option.quantity) {
      throw new OptionError(
        'Cannot calculate position metrics without purchase price and quantity',
      );
    }

    const baseMetrics = this.getMetrics(
      option,
      spotPrice,
      currentTime,
      volatility,
    );
    const totalCost = option.purchasePrice * option.quantity;
    const currentValue = baseMetrics.currentPrice * option.quantity;
    const pnlPerContract = baseMetrics.currentPrice - option.purchasePrice;
    const pnlTotal = pnlPerContract * option.quantity;
    const roi = totalCost > 0 ? pnlTotal / totalCost : 0;
    const profitable = this.isProfitable(option, spotPrice);

    // Expiry calculations (intrinsic value only)
    const expiryValue = baseMetrics.intrinsicValue * option.quantity;
    const expiryPnl = expiryValue - totalCost;
    const expiryRoi = totalCost > 0 ? expiryPnl / totalCost : 0;

    // Status calculation
    const inTheMoney = baseMetrics.inTheMoney;
    const nearBreakeven = baseMetrics.breakeven
      ? Math.abs(
          (spotPrice - baseMetrics.breakeven) / (option.strikePrice || 1),
        ) < 0.005
      : false;

    let statusLabel = profitable ? 'Profitable' : inTheMoney ? 'ITM' : 'OTM';
    let statusColor = profitable
      ? 'var(--brand-green)'
      : nearBreakeven
        ? '#ffd166'
        : inTheMoney
          ? '#00b37a'
          : 'var(--brand-red)';

    if (baseMetrics.expired) {
      statusLabel = 'Expired';
      statusColor = '#808080';
    }

    return {
      ...baseMetrics,
      purchasePrice: option.purchasePrice,
      quantity: option.quantity,
      totalCost,
      currentValue,
      pnlPerContract,
      pnlTotal,
      roi,
      profitable,
      expiryValue,
      expiryPnl,
      expiryRoi,
      statusLabel,
      statusColor,
    };
  }

  static settleOption(
    option: Option,
    expirySpotPrice: number,
  ): SettlementResult {
    if (!option.purchasePrice || !option.quantity) {
      throw new OptionError(
        'Cannot settle option without purchase price and quantity',
      );
    }
    if (expirySpotPrice <= 0) {
      throw new OptionError('Expiry spot price must be positive');
    }

    const intrinsicValue = this.getIntrinsicValue(option, expirySpotPrice);
    const pnlPerContract = intrinsicValue - option.purchasePrice;
    const pnlTotal = pnlPerContract * option.quantity;

    return {
      intrinsicValue,
      pnlPerContract,
      pnlTotal,
    };
  }
}

// Export getMinOptionPrice function for use in other services
export const getMinOptionPrice = OptionCalculator.getMinOptionPrice.bind(OptionCalculator);

// Utility functions for display
export function getSideLabel(
  optionType: OptionType,
  advancedMode: boolean,
): string {
  return advancedMode ? optionType : optionType === 'CALL' ? 'ABOVE' : 'BELOW';
}

export function getLetterForOption(
  optionType: OptionType,
  advancedMode?: boolean,
): string {
  return advancedMode ? optionType[0] : optionType === 'CALL' ? 'A' : 'B';
}
